/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useState, useEffect } from 'react';
import FaqHero from './components/FaqHero';
import CategoryTabs from './components/CategoryTabs';
import FaqAccordion from './components/FaqAccordion';
import FaqSidebar from './components/FaqSidebar';
import FaqAskQuestion from './components/FaqAskQuestion';
import FaqCta from './components/FaqCta';
import FaqBottomCta from './components/FaqBottomCta';
import { faqItems, faqCategories } from '../../mocks/faqData';
import { useSEOHead } from '../../hooks/useSEOHead';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import { CORE_BUSINESS, SCHEMA_IDS } from '../../lib/coreSchemas';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { Link } from 'react-router-dom';

const BASE = 'https://rockhoundauto.com';

export default function FaqPage() {
  useSEOHead({
    title: 'Real Answers. No Dealer Spin. | FAQ | Rockhound Auto',
    description: 'Frequently asked questions about buying used cars in Warrenton, Astoria, Seaside, Tillamook, Oregon and SW Washington. Straight talk, no runaround.',
    keywords: 'FAQ used cars Warrenton Oregon, car buying questions Astoria, auto broker FAQ Seaside Tillamook, financing questions Pacific Northwest, trade-in FAQ Oregon Coast',
    canonicalUrl: `${BASE}/faq`,
  });

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const categories = ['all', ...faqCategories.map((c) => c.id)];

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && categories.includes(hash)) {
      setSelectedCategory(hash);
      setTimeout(() => {
        const element = document.getElementById(`category-${hash}`);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, []);

  useEffect(() => {
    // ── FAQPage schema with proper @id cross-references ──
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${BASE}/faq#faqpage`,
      name: 'Frequently Asked Questions - Rockhound Auto',
      description: 'Common questions about buying used cars, financing, trade-ins, and auto broker services at Rockhound Auto in Warrenton, Oregon.',
      url: `${BASE}/faq`,
      isPartOf: { '@id': SCHEMA_IDS.website },
      about: { '@id': SCHEMA_IDS.localBusiness },
      publisher: { '@id': SCHEMA_IDS.organization },
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
          author: { '@id': SCHEMA_IDS.organization },
        },
      })),
      breadcrumb: { '@id': `${BASE}/faq#breadcrumb` },
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['.faq-question', '.faq-answer', '.speakable-content'],
      },
      contactPoint: { '@id': SCHEMA_IDS.contactPoint },
      keywords: 'car buying FAQ, used car questions, financing questions, trade-in FAQ, dealer questions, auto broker FAQ, Warrenton Oregon',
      inLanguage: 'en-US',
      datePublished: '2023-01-15',
      dateModified: new Date().toISOString().split('T')[0],
    };

    // ── WebPage schema ──
    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${BASE}/faq#webpage`,
      name: 'FAQ | Used Car Buying Questions | Rockhound Auto Warrenton OR',
      description: 'Answers to frequently asked questions about buying used cars, financing, trade-ins, and auto broker services at Rockhound Auto.',
      url: `${BASE}/faq`,
      isPartOf: { '@id': SCHEMA_IDS.website },
      about: { '@id': SCHEMA_IDS.localBusiness },
      publisher: { '@id': SCHEMA_IDS.organization },
      mainEntity: { '@id': `${BASE}/faq#faqpage` },
      breadcrumb: { '@id': `${BASE}/faq#breadcrumb` },
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['.speakable-headline', '.speakable-summary', '.speakable-content'],
      },
      contactPoint: { '@id': SCHEMA_IDS.contactPoint },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    };

    // ── BreadcrumbList schema ──
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${BASE}/faq#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE },
        { '@type': 'ListItem', position: 2, name: 'FAQ', item: `${BASE}/faq` },
      ],
    };

    injectJsonLd('schema-faq-page', faqSchema);
    injectJsonLd('schema-faq-webpage', webPageSchema);
    injectJsonLd('schema-faq-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-faq-page');
      removeJsonLd('schema-faq-webpage');
      removeJsonLd('schema-faq-breadcrumb');
    };
  }, []);

  const filteredFaqs = selectedCategory === 'all' ? faqItems : faqItems.filter((item) => item.category === selectedCategory);
  const searchedFaqs = searchQuery ? filteredFaqs.filter((faq) => faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase())) : filteredFaqs;

  return (
    <>
      <Breadcrumb items={[{ label: 'FAQ' }]} />
      <>
        <FaqHero searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        <div className="bg-[#F7F5F0] min-h-screen py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-1">
                <FaqSidebar categories={categories} selectedCategory={selectedCategory} onCategoryChange={setSelectedCategory} />
              </div>
              <div className="lg:col-span-3">
                <div className="lg:hidden mb-6">
                  <CategoryTabs categories={categories} selectedCategory={selectedCategory} onCategoryChange={setSelectedCategory} />
                </div>
                <div className="mb-6" id={`category-${selectedCategory}`}>
                  <h2 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E]">{searchedFaqs.length} {searchedFaqs.length === 1 ? 'Question' : 'Questions'} Found</h2>
                </div>
                {searchedFaqs.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-[#E8E2D6] p-12 text-center">
                    <i className="ri-question-line text-6xl text-gray-300 mb-4 block"></i>
                    <h3 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E] mb-2">No questions found</h3>
                    <p className="text-gray-600 mb-6">Try adjusting your search or browse all categories</p>
                    <button onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }} className="px-6 py-3 bg-[#1C2B1E] text-white rounded-full font-medium hover:bg-[#2d4430] transition-colors whitespace-nowrap cursor-pointer">View All Questions</button>
                  </div>
                ) : (
                  <FaqAccordion faqs={searchedFaqs} />
                )}
                <div className="mt-8"><FaqAskQuestion /></div>
              </div>
            </div>
            <div className="mt-12"><FaqCta /></div>
          </div>
        </div>
        <FaqBottomCta />
      </>
    </>
  );
}